/**
 * Codex Design System — MUI Component Overrides
 *
 * Applies Codex tokens to every MUI component.
 * Each component section references the Figma Foundations spec where relevant.
 *
 * Adding a new override:
 *   1. Add the MUI component key (e.g. MuiAccordionSummary)
 *   2. Use only tokens from SemanticColors, radii, spacingTokens,
 *      fontFamilies, fontWeights, durations, easings, and the shadow set.
 *   3. Never hard-code hex values — always reference a token.
 */

import { alpha } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';
import { palettes } from '../tokens/palettes';
import { fontFamilies, fontWeights, pxToRem } from '../tokens/typography';
import { spacingTokens, radii } from '../tokens/spacing';
import { durations, easings } from '../tokens/motion';
import type { ShadowSet } from '../tokens/elevation';
import { buildCssVariables } from './css-variables';
import type { SemanticColors } from '../semantic/types';

export function buildComponents(c: SemanticColors, shadows: ShadowSet): ThemeOptions['components'] {
  const isLight = c.mode === 'light';

  return {
    // ── Global baseline + CSS custom properties ──────────────────────────────
    MuiCssBaseline: {
      styleOverrides: () => `
        ${buildCssVariables(c)}
        *, *::before, *::after { box-sizing: border-box; }
        html {
          font-size: 16px;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        body {
          background-color: ${c.bgBody};
          color: ${c.textPrimary};
        }
        *:focus-visible {
          outline: 3px solid ${c.borderFocus};
          outline-offset: 2px;
        }
      `,
    },

    // ── Button (Figma Foundations §7.2 component reference) ─────────────────
    MuiButton: {
      defaultProps: { disableElevation: false, disableRipple: false },
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          fontFamily: fontFamilies.body,
          fontWeight: fontWeights.medium,
          fontSize: pxToRem(14),
          letterSpacing: '0.04em',
          textTransform: 'none' as const,
          minHeight: 44, // WCAG 2.5.5 touch target
          transition: `all ${durations.shorter}ms ${easings.sharp}`,
          '&:focus-visible': {
            outline: `3px solid ${c.borderFocus}`,
            outlineOffset: '2px',
          },
        },
        sizeSmall: { padding: '6px 14px', fontSize: pxToRem(13), minHeight: 36 },
        sizeMedium: { padding: '10px 20px' },
        sizeLarge: { padding: '14px 28px', fontSize: pxToRem(15), minHeight: 48 },
        contained: {
          boxShadow: shadows.sm,
          '&:hover': { boxShadow: shadows.md },
          '&:active': { boxShadow: 'none' },
        },
        outlined: {
          borderColor: c.borderDefault,
          '&:hover': {
            backgroundColor: alpha(c.primary, 0.04),
            borderColor: c.primary,
            boxShadow: shadows.sm,
          },
        },
        text: {
          '&:hover': { backgroundColor: alpha(c.primary, 0.04) },
        },
      },
    },

    // ── IconButton ───────────────────────────────────────────────────────────
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          minWidth: 44,
          minHeight: 44,
          transition: `background-color ${durations.shortest}ms ${easings.easeOut}`,
          '&:focus-visible': {
            outline: `3px solid ${c.borderFocus}`,
            outlineOffset: '2px',
          },
        },
        sizeSmall: { minWidth: 36, minHeight: 36 },
      },
    },

    // ── FAB — radius.xl resting md, hover lg, active sm ─────────────────────
    MuiFab: {
      styleOverrides: {
        root: {
          borderRadius: radii.xl,
          boxShadow: shadows.md,
          textTransform: 'none' as const,
          '&:hover': { boxShadow: shadows.lg },
          '&:active': { boxShadow: shadows.sm },
        },
      },
    },

    // ── Paper ────────────────────────────────────────────────────────────────
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: radii.md,
          transition: `box-shadow ${durations.standard}ms ${easings.easeInOut}`,
        },
        rounded: { borderRadius: radii.md },
        elevation0: { boxShadow: shadows.none },
        elevation1: { boxShadow: shadows.sm },
        elevation2: { boxShadow: shadows.sm },
        elevation3: { boxShadow: shadows.sm },
        elevation4: { boxShadow: shadows.md },
        elevation8: { boxShadow: shadows.lg },
        elevation16: { boxShadow: shadows.xl },
        elevation24: { boxShadow: shadows.xl },
      },
    },

    // ── Card — resting sm, hover md ──────────────────────────────────────────
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          boxShadow: shadows.sm,
          backgroundColor: isLight ? c.bgElevated : c.bgSurface,
          border: `1px solid ${c.borderSubtle}`,
          transition: `box-shadow ${durations.standard}ms ${easings.easeInOut}`,
          '&:hover': { boxShadow: shadows.md },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: spacingTokens[4],
          '&:last-child': { paddingBottom: spacingTokens[4] },
        },
      },
    },

    // ── TextField / Input ────────────────────────────────────────────────────
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'medium' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          fontSize: pxToRem(14),
          transition: `border-color ${durations.shortest}ms ${easings.easeOut}`,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: c.borderDefault,
            transition: `border-color ${durations.shortest}ms ${easings.easeOut}`,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(c.primary, 0.5) },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: c.primary,
            borderWidth: 2,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: c.error },
        },
        input: {
          padding: '12px 16px',
          minHeight: 44,
          boxSizing: 'border-box',
          '&::placeholder': { color: c.textPlaceholder, opacity: 1 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: pxToRem(14),
          color: c.textSecondary,
          '&.Mui-focused': { color: c.primary },
          '&.Mui-error': { color: c.error },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: pxToRem(12), marginTop: spacingTokens[1] },
      },
    },

    // ── Select ───────────────────────────────────────────────────────────────
    MuiSelect: {
      styleOverrides: {
        select: { minHeight: 44, boxSizing: 'border-box' },
      },
    },

    // ── Checkbox, Radio, Switch ───────────────────────────────────────────────
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: spacingTokens[2],
          '&:focus-visible': {
            outline: `3px solid ${c.borderFocus}`,
            outlineOffset: '2px',
            borderRadius: radii.sm,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: { root: { padding: spacingTokens[2] } },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { padding: spacingTokens[2] },
        thumb: { boxShadow: shadows.sm },
        track: { borderRadius: radii.full },
      },
    },

    // ── Chip — radius.sm ─────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radii.sm,
          fontWeight: fontWeights.medium,
          fontSize: pxToRem(13),
          height: 28,
        },
        sizeSmall: { height: 24, fontSize: pxToRem(12) },
      },
    },

    // ── Avatar — radius.full (always circular) ────────────────────────────────
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: 40,
          height: 40,
          fontSize: pxToRem(16),
          fontWeight: fontWeights.medium,
          borderRadius: radii.full,
        },
      },
      variants: [
        { props: { variant: 'rounded' as const }, style: { borderRadius: radii.md } },
        { props: { variant: 'square' as const }, style: { borderRadius: radii.none } },
      ],
    },

    // ── Badge ─────────────────────────────────────────────────────────────────
    MuiBadge: {
      styleOverrides: {
        badge: {
          borderRadius: radii.sm,
          fontWeight: fontWeights.semibold,
          fontSize: pxToRem(12),
          padding: '2px 8px',
          minWidth: 20,
        },
      },
    },

    // ── Tooltip — radius.sm, elevation md ────────────────────────────────────
    MuiTooltip: {
      defaultProps: { arrow: true, enterDelay: 200, leaveDelay: 0 },
      styleOverrides: {
        tooltip: {
          backgroundColor: isLight ? palettes.pumice[800] : palettes.pumice[200],
          color: isLight ? '#FFFFFF' : '#212121',
          fontSize: pxToRem(12),
          fontWeight: fontWeights.regular,
          borderRadius: radii.sm,
          padding: `${spacingTokens[1]}px ${spacingTokens[2]}px`,
          boxShadow: shadows.md,
        },
        arrow: {
          color: isLight ? palettes.pumice[800] : palettes.pumice[200],
        },
      },
    },

    // ── Dialog — radius.lg, elevation xl ─────────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radii.lg,
          boxShadow: shadows.xl,
          backgroundColor: isLight ? c.bgElevated : c.bgElevatedXl,
          padding: 0,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: fontFamilies.heading,
          fontWeight: fontWeights.semibold,
          fontSize: pxToRem(20),
          padding: `${spacingTokens[5]}px ${spacingTokens[6]}px ${spacingTokens[3]}px`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: `${spacingTokens[4]}px ${spacingTokens[6]}px` },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: `${spacingTokens[3]}px ${spacingTokens[6]}px ${spacingTokens[5]}px`,
          gap: spacingTokens[2],
        },
      },
    },

    // ── Drawer — elevation xl ─────────────────────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxShadow: shadows.xl,
          backgroundImage: 'none',
          backgroundColor: isLight ? c.bgElevated : c.bgSurface, // bgSurface = #1F1F1F matches original sidebar
        },
      },
    },

    // ── AppBar ────────────────────────────────────────────────────────────────
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: shadows.sm,
          borderBottom: `1px solid ${c.borderSubtle}`,
        },
        colorDefault: {
          backgroundColor: isLight ? c.bgElevated : c.bgSurface,
          color: c.textPrimary,
        },
        colorPrimary: {
          backgroundColor: isLight ? c.bgElevated : c.bgSurface,
          color: c.textPrimary,
        },
      },
    },

    // ── Tabs ──────────────────────────────────────────────────────────────────
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 3, borderRadius: '3px 3px 0 0' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: fontWeights.medium,
          fontSize: pxToRem(14),
          minHeight: 48,
          transition: `color ${durations.shortest}ms ${easings.easeOut}`,
          '&:focus-visible': {
            outline: `3px solid ${c.borderFocus}`,
            outlineOffset: '-3px',
            borderRadius: radii.sm,
          },
        },
      },
    },

    // ── Menu / Popover — elevation lg ─────────────────────────────────────────
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radii.md,
          boxShadow: shadows.lg,
          backgroundColor: isLight ? c.bgElevated : c.bgElevatedLg,
          border: `1px solid ${c.borderSubtle}`,
          marginTop: spacingTokens[1],
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: pxToRem(14),
          minHeight: 44,
          padding: `${spacingTokens[2]}px ${spacingTokens[4]}px`,
          borderRadius: radii.sm,
          margin: `0 ${spacingTokens[1]}px`,
          transition: `background-color ${durations.shortest}ms ${easings.easeOut}`,
          '&:focus-visible': {
            outline: `3px solid ${c.borderFocus}`,
            outlineOffset: '-3px',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: radii.md,
          boxShadow: shadows.lg,
          backgroundColor: isLight ? c.bgElevated : c.bgElevatedLg,
          border: `1px solid ${c.borderSubtle}`,
        },
      },
    },

    // ── List ──────────────────────────────────────────────────────────────────
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.sm,
          minHeight: 44,
          transition: `background-color ${durations.shortest}ms ${easings.easeOut}`,
          '&.Mui-selected': {
            backgroundColor: alpha(c.primary, 0.08),
            '&:hover': { backgroundColor: alpha(c.primary, 0.12) },
          },
        },
      },
    },

    // ── Divider ───────────────────────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: c.borderDefault },
        light: { borderColor: c.borderSubtle },
      },
    },

    // ── Alert ─────────────────────────────────────────────────────────────────
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          fontSize: pxToRem(14),
          padding: `${spacingTokens[3]}px ${spacingTokens[4]}px`,
        },
        standardSuccess: {
          backgroundColor: alpha(c.success, 0.1),
          color: isLight ? palettes.laurel[800] : palettes.laurel[200],
        },
        standardError: {
          backgroundColor: alpha(c.error, 0.1),
          color: isLight ? palettes.cinnabar[800] : palettes.cinnabar[200],
        },
        standardWarning: {
          backgroundColor: alpha(c.warning, 0.1),
          color: isLight ? palettes.saffron[800] : palettes.saffron[200],
        },
        standardInfo: {
          backgroundColor: alpha(c.info, 0.1),
          color: isLight ? palettes.lapis[800] : palettes.lapis[200],
        },
      },
    },

    // ── Snackbar / Toast — elevation lg ───────────────────────────────────────
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          boxShadow: shadows.lg,
          fontSize: pxToRem(14),
          backgroundColor: isLight ? palettes.pumice[800] : palettes.pumice[100],
          color: isLight ? '#FFFFFF' : '#212121',
        },
      },
    },

    // ── Skeleton ───────────────────────────────────────────────────────────────
    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: radii.sm },
        rounded: { borderRadius: radii.md },
        circular: { borderRadius: radii.full },
      },
    },

    // ── Table — radius.none (always sharp) ────────────────────────────────────
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: pxToRem(14),
          borderBottomColor: c.borderDefault,
          padding: `${spacingTokens[3]}px ${spacingTokens[4]}px`,
          borderRadius: radii.none,
        },
        head: {
          fontWeight: fontWeights.semibold,
          fontSize: pxToRem(12),
          letterSpacing: '0.02em',
          color: c.textSecondary,
        },
      },
    },

    // ── Accordion ─────────────────────────────────────────────────────────────
    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          border: `1px solid ${c.borderDefault}`,
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },

    // ── Breadcrumbs ───────────────────────────────────────────────────────────
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { fontSize: pxToRem(14) },
        separator: { color: c.textDisabled },
      },
    },

    // ── Pagination ────────────────────────────────────────────────────────────
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: radii.sm,
          minWidth: 36,
          minHeight: 36,
          '&.Mui-selected': { fontWeight: fontWeights.semibold },
        },
      },
    },

    // ── Stepper ───────────────────────────────────────────────────────────────
    MuiStepIcon: {
      styleOverrides: {
        root: {
          fontSize: pxToRem(24),
          '&.Mui-completed': { color: c.success },
        },
      },
    },

    // ── Slider ────────────────────────────────────────────────────────────────
    MuiSlider: {
      styleOverrides: {
        root: { height: 4 },
        thumb: {
          width: 20,
          height: 20,
          '&:focus-visible, &:hover': { boxShadow: `0 0 0 8px ${alpha(c.primary, 0.16)}` },
        },
        track: { borderRadius: radii.full },
        rail: { borderRadius: radii.full, opacity: 0.3 },
      },
    },

    // ── Progress ──────────────────────────────────────────────────────────────
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: radii.full, height: 6 },
        bar: { borderRadius: radii.full },
      },
    },

    // ── Link ──────────────────────────────────────────────────────────────────
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: {
        root: {
          color: c.textLink,
          fontWeight: fontWeights.medium,
          transition: `color ${durations.shortest}ms ${easings.easeOut}`,
          '&:focus-visible': {
            outline: `3px solid ${c.borderFocus}`,
            outlineOffset: '2px',
            borderRadius: '2px',
          },
        },
      },
    },
  };
}
